import * as _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../FeatureFlags';
import { ListResult } from '../fetchers/ListResult';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { IRemoteRefreshAgentInfo } from '../models/IRemoteRefreshAgentInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { OAuthCredentials } from '../../connectivity/resources/OAuthCredentials';
import { ServerUsers } from '../../userManagement/services/resources/ServerUsers';
import { IConnectedDevice } from '../ServerApi/types/IConnectedDevice';
import { IOAuthCredential, IOAuthCredentialTypeInfo } from '../ServerApi/types/IOAuthCredential';
import { AddOAuthCredentialAction } from '../../connectivity/services/AddOAuthCredentialAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from '../services/ConfirmActionDialog';
import { OAuthCredentialsService } from '../../connectivity/services/OAuthCredentialsService';
import { PersonalAccessTokensService } from '../services/PersonalAccessTokensService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { is3rdParty } from '../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { UserSettingsCtrlCommon } from '../services/UserSettingsCtrlCommon';
import { CredentialTypeFilter } from '../../userSettings/services/filters/CredentialTypeFilter';
import { showDeleteSavedPasswordActionDialog } from '../siteManagement/services/DeleteMyCredentialsAction';
import { showDeleteOAuthCredentialsDialog } from '../siteManagement/services/DeleteOAuthCredentialsAction';
import { showDeleteConnectedDeviceDialog } from '../../userSettings/services/DeleteConnectedDeviceAction';
import { ConnectedDeviceSectionProps } from '../../userSettings/components/ConnectedClientsSection';
import { SecuritySettingsProps } from '../../userSettings/components/SecuritySettings';

export class SiteUserSettingsSubspaceCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'UserSettingsCtrlCommon',

    // Local Services
    'ConfirmActionDialog',
    'PersonalAccessTokensService',
  ];

  constructor(
    private $scope: SiteUserSettingsSubspaceCtrl.Scope,
            $translate: ng.translate.ITranslateService,
            userSettingsCtrlCommon: UserSettingsCtrlCommon,
            ConfirmActionDialog: ConfirmActionDialog,
            personalAccessTokensService: PersonalAccessTokensService
             ) {

    // These come from the parent AngularDirective in SiteUserSettingsSubspace.tsx
    const isSessionUserSelf = $scope.isSessionUserSelf;
    const userSettings = $scope.userSettings;
    const userRefreshTokenCount = $scope.userRefreshTokenCount;
    const serverUser = $scope.serverUser;
    const siteUser = $scope.siteUser;

    this.failureNotificationContent();
    userSettingsCtrlCommon.initScope($scope, isSessionUserSelf, serverUser, siteUser, userSettings, userRefreshTokenCount);

    // TODO: Replace with a session variable check.  See the fix from http://fogbugz/default.asp?142850 for more details
    $scope.canManageCredentials = true;

    $scope.isSAMLUser = !!siteUser && !!siteUser.authenticationProviderId && is3rdParty(siteUser.authenticationProviderId);

    $scope.securitySettingsProps = {
      serverUser: serverUser,
      isViewerSelf: $scope.isViewerSelf,
      isServerUserSubplace: false
    };

    // Connected devices

    const refreshTokensCallback = personalAccessTokensService.loadTokensIntoScope(<any>$scope, serverUser);

    $scope.deleteConnectedDevice = (device: IConnectedDevice) =>
    showDeleteConnectedDeviceDialog(device, serverUser.id)
        .then(refreshConnectedDevices);

    $scope.connectedDeviceSectionProps = {
      serverUser: serverUser,
    };

    const refreshConnectedDevices = () => ServerUsers.instance.getUserConnectedDevices(serverUser.id)
      .then((devices: IConnectedDevice[]) => {
        $scope.connectedDevices = devices;
      }).then(refreshTokensCallback);

    personalAccessTokensService.loadActionsIntoScope(<any>$scope, serverUser, isSessionUserSelf, refreshConnectedDevices);

    refreshConnectedDevices();

    // Credentials list

    // TODO: Filter out invalid credentials, needs server support
    $scope.credentialsLists = this.createOAuthCredentialLists();

    $scope.credentialTypeFilter = CredentialTypeFilter.filter;

    const refreshCredentials = () => {
      SliceableHelper.fetchAll(OAuthCredentials.all())
        .then<void>((credentialsListResult: ListResult<IOAuthCredential>) => {
          $scope.credentialsLists = this.createOAuthCredentialLists(credentialsListResult.items);
        });
    };

    refreshCredentials();

    // Initialize credential actions
    const getOauthServerRoleAndRedirect = (typeInfo: IOAuthCredentialTypeInfo) => {
        const displayscope = <SiteUserSettingsSubspaceCtrl.OauthUrlScope>$scope.$new();
        if(typeInfo.type === 'snowflake')
        {
          displayscope.supportsOAuthRole = true;
        }
        displayscope.oAuthInstanceUrl = $translate.instant('enterServer');
        displayscope.input = {
            text: '',
            maxLength: 200
        };
        displayscope.oAuthRole = $translate.instant('addRole');
        displayscope.input2 = {
          text: '',
          maxLength: 100
        };
        displayscope.withAuthenticationSubHeader = true;
        const dialogInstance = ConfirmActionDialog.open({
            title: $translate.instant('addNewSavedCredentialTitle'),
            bodyTemplateUrl: 'addNewOAuthSavedCredentialDialog.html',
            scope: displayscope,
            position: { reserveHeight: 250 },
            confirmDisabled: () => _.isEmpty(displayscope.input.text),
            actionName:$translate.instant('Add'),
            action: (dialogInstance: IConfirmActionDialogInstance) => {
                const input = displayscope.input.text;
                var instanceUrl;
                if (input.startsWith('https://')  ||  input.startsWith('http://')){
                  instanceUrl = input;
                }else{
                  instanceUrl = 'https://' + input;
                }
                AddOAuthCredentialAction.attemptAuth(typeInfo.type, instanceUrl,
                displayscope.input2.text).then(refreshCredentials);
            }
        });
    };
    $scope.deleteAllCredentials = () =>
      showDeleteSavedPasswordActionDialog().then(refreshCredentials);

    $scope.addCredentialHref = (typeInfo: IOAuthCredentialTypeInfo) => AddOAuthCredentialAction.href(typeInfo);
    $scope.addCredential = (typeInfo: IOAuthCredentialTypeInfo) => {
       //TFS834312 - For Snowflake OAuth, we need InstanceUrl and the role to connect with to redirect to Oauth Page
       //TFS1137015 - [Pluggable OAuth on Server] Add support for instanceUrl
       if (typeInfo.type === 'snowflake' || typeInfo.supportsCustomDomain === true) {
            getOauthServerRoleAndRedirect(typeInfo);
        }
        else {
            AddOAuthCredentialAction.attemptAuth(typeInfo.type).then(refreshCredentials);
        }
    };

    $scope.deleteCredential = (oauthCredential: SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel) =>
    showDeleteOAuthCredentialsDialog(Set.fromIdItemArray([oauthCredential.credential]))
        .then(refreshCredentials);

    $scope.testCredential = (oauthCredential: SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel) => this.testCredential(oauthCredential);

    $scope.$on('$destroy', () => AddOAuthCredentialAction.cancelPendingAuthAttempt());
  }

  public failureNotificationContent() {
    if (getSessionManagementServiceInstance().flowsOnSiteEnabled()) {
      this.$scope.failureNotificationTitle = Messages.extractsOrFlowEmailNotificationSettingSection();
      this.$scope.failureNotificationDetail = Messages.extractsOrFlowEmailNotificationUserSettingDetail();
      this.$scope.failureNotificationCheckboxLabel = Messages.extractsOrFlowEmailNotificationUserSettingEnable();
    } else {
      this.$scope.failureNotificationTitle = Messages.extractsEmailNotificationSettingSection();
      this.$scope.failureNotificationDetail = Messages.extractsEmailNotificationUserSettingDetail();
      this.$scope.failureNotificationCheckboxLabel = Messages.extractsEmailNotificationUserSettingEnable();
    }
  }

  private testCredential(oauthCredential: SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel) {
    oauthCredential.test = { inProgress: true };

    const handleTestResult = (success: any) => {
      oauthCredential.test = {
        success: !!success
      };
    };

    OAuthCredentials.test(oauthCredential.credential.id)
      .then(handleTestResult, () => handleTestResult(false));
  }

  private createOAuthCredentialLists(credentials?: IOAuthCredential[]): SiteUserSettingsSubspaceCtrl.OAuthCredentialsList[] {
    const convertToCredentialViewModel = (credential: IOAuthCredential): SiteUserSettingsSubspaceCtrl.OAuthCredentialViewModel => {
      return {credential: credential, test: { enabled: OAuthCredentialsService.supportsTestConnection(credential.type) }};
    };

    const credentialVMGroupsMap = _.groupBy(credentials, c => c.type);

    // Iterate through all OAuth types, if any is enabled, put it in  the result
    return _.compact(_.map(OAuthCredentialsService.getAllOAuthCredentialTypes(),
      (typeInfo: IOAuthCredentialTypeInfo) => {
        const oauthCredentialsList: SiteUserSettingsSubspaceCtrl.OAuthCredentialsList = {
          typeInfo: typeInfo,
          addEnabled: OAuthCredentials.canAddForConnectionType(typeInfo),
          credentials: _.map(credentialVMGroupsMap[typeInfo.type], convertToCredentialViewModel)
        };

        return (oauthCredentialsList.addEnabled || oauthCredentialsList.credentials.length > 0) ? oauthCredentialsList : null;
      })
    );
  }
}

export module SiteUserSettingsSubspaceCtrl {

  export interface OAuthCredentialViewModel  {
    credential: IOAuthCredential;
    test: {
      inProgress?: boolean;
      success?: boolean;
      // Determine if test button is displayed to customers, as of Q4 2016, we disable them for
      // generic-auth-based WDC credentials. TFS 546010
      enabled?: boolean;
    };
  }

  export interface OAuthCredentialsList {
    typeInfo: IOAuthCredentialTypeInfo;
    addEnabled: boolean;
    credentials?: OAuthCredentialViewModel[];
  }

  export interface RemoteRefreshAgentsScope extends ng.IScope {
    deleteRemoteRefreshAgent(agent: IRemoteRefreshAgentInfo, $event: JQueryEventObject): void;
    remoteRefreshAgents: IRemoteRefreshAgentInfo[];
  }

  export interface OauthUrlScope extends ng.IScope {
      oAuthInstanceUrl: string;
      input: {
          text: string;
          maxLength?: number;
      };
      oAuthRole: string;
      input2: {
        text: string;
        maxLength?: number;
      };
      withAuthenticationSubHeader?: boolean;
      supportsOAuthRole?: boolean;
  }

  export interface Scope extends UserSettingsCtrlCommon.Scope, RemoteRefreshAgentsScope {
    // Credentials

    canManageCredentials: boolean;

    deleteAllCredentials($event: JQueryEventObject): void;

    addCredential(typeInfo: IOAuthCredentialTypeInfo): void;
    addCredentialHref(typeInfo: IOAuthCredentialTypeInfo): string;

    testCredential(oauthCredential: OAuthCredentialViewModel): any;
    deleteCredential(oauthCredential: OAuthCredentialViewModel): void;

    credentialsLists: OAuthCredentialsList[];
    credentialTypeFilter(credentialTypeInfo: IOAuthCredentialTypeInfo): string;

    objectName: string;

    isSAMLUser: boolean;

    remoteAgentManagerEnabled: boolean;

    connectedDeviceSectionProps: ConnectedDeviceSectionProps;
    securitySettingsProps: SecuritySettingsProps;
    connectedDevices: IConnectedDevice[];
    deleteConnectedDevice(token: IConnectedDevice): void;
    failureNotificationTitle: string;
    failureNotificationDetail: string;
    failureNotificationCheckboxLabel: string;

    userRefreshTokenCount: number;
    isSessionUserSelf: boolean;
  }
}

Module.instance().controller('SiteUserSettingsSubspaceCtrl', SiteUserSettingsSubspaceCtrl);
